import { Box, Button, Flex, Heading, Icon, SimpleGrid, Spinner, Stack, Text } from "@chakra-ui/react";
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { IconType } from "react-icons";
import { FiArchive, FiClipboard, FiFileText } from "react-icons/fi";
import {
  ContractReportSortColumn,
  ContractStatusDto,
  NeedStatus,
  SortDirection,
  useListBuyerEventsQuery,
  useListContractReportsQuery,
  useListNeedsQuery,
} from "../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../auth/useLoggedInWithOrgContextState";
import { displayDate } from "../formatting/displayDate";
import { base64Encode } from "../utils/base64";
import type { EmptyConversationActions } from "./AssistantChat";

export type Starting =
  | { kind: "need"; id: string; title: string }
  | { kind: "procurement"; id: string; title: string }
  | { kind: "contract"; id: string; title: string }
  | { kind: "nothing" };

type Props = {
  actions: EmptyConversationActions;
  onStart: (starting: Starting) => void;
  isStarting: boolean;
};

const PICK_LIMIT = 10;

export const WhatDoYouHave = ({ actions, onStart, isStarting }: Props) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState<"need" | "earlier" | null>(null);
  const disabled = actions.disabled || isStarting;
  const toggle = (card: "need" | "earlier") => setOpen((current) => (current === card ? null : card));
  const startFromDocuments = () => {
    setOpen(null);
    actions.writeInComposer();
    actions.pickFiles?.();
  };

  return (
    <Stack spacing="4" py="4" px="2">
      <Heading size="md" color="smText" textAlign="center">
        {t("What do you have?")}
      </Heading>
      <Text fontSize="sm" color="smMuted" textAlign="center">
        {t("Start from what you already have, and the AI fills in everything it can from it.")}
      </Text>
      <SimpleGrid minChildWidth="200px" spacing="3">
        <StartCard
          icon={FiClipboard}
          label={t("A need")}
          detail={t("A need that is ready to be procured")}
          isActive={open === "need"}
          onClick={() => toggle("need")}
          isDisabled={disabled}
        />
        <StartCard
          icon={FiFileText}
          label={t("Documents or a short description")}
          detail={t("Attach PDF or Word, or describe what you are buying")}
          isActive={false}
          onClick={startFromDocuments}
          isDisabled={disabled}
        />
        <StartCard
          icon={FiArchive}
          label={t("An earlier procurement or contract")}
          detail={t("Contracts that expire soon come first")}
          isActive={open === "earlier"}
          onClick={() => toggle("earlier")}
          isDisabled={disabled}
        />
      </SimpleGrid>
      {open === "need" && <NeedPicker onPick={onStart} isDisabled={disabled} />}
      {open === "earlier" && <EarlierPicker onPick={onStart} isDisabled={disabled} />}
      {isStarting && (
        <Flex justify="center">
          <Spinner size="sm" color="smPrimary" />
        </Flex>
      )}
      <Flex justify="center">
        <Button
          variant="link"
          colorScheme="teal"
          size="sm"
          fontWeight="normal"
          onClick={() => onStart({ kind: "nothing" })}
          isDisabled={disabled}
        >
          {t("I have nothing to start from")}
        </Button>
      </Flex>
    </Stack>
  );
};

const StartCard = ({
  icon,
  label,
  detail,
  isActive,
  onClick,
  isDisabled,
}: {
  icon: IconType;
  label: string;
  detail: string;
  isActive: boolean;
  onClick: () => void;
  isDisabled: boolean;
}) => (
  <Button
    variant={isActive ? "solid" : "outline"}
    colorScheme="teal"
    h="auto"
    py="4"
    px="4"
    whiteSpace="normal"
    flexDirection="column"
    alignItems="flex-start"
    textAlign="left"
    gap="1"
    onClick={onClick}
    isDisabled={isDisabled}
    aria-expanded={isActive}
  >
    <Flex align="center" gap="2">
      <Icon as={icon} />
      <Text as="span" fontWeight="semibold">
        {label}
      </Text>
    </Flex>
    <Text as="span" fontSize="sm" fontWeight="normal">
      {detail}
    </Text>
  </Button>
);

type PickQuery = { isLoading: boolean; isError: boolean; refetch: () => unknown };

const PickList = ({
  heading,
  query,
  emptyText,
  total,
  onShowAll,
  children,
}: {
  heading: string;
  query: PickQuery;
  emptyText: string;
  total: number;
  onShowAll: () => void;
  children: ReactNode[];
}) => {
  const { t } = useTranslation();
  return (
    <Box>
      <Text fontSize="xs" color="smMuted" fontWeight="medium" textTransform="uppercase" letterSpacing="wide" mb="1">
        {heading}
      </Text>
      {query.isLoading ? (
        <Spinner size="sm" color="smPrimary" />
      ) : query.isError ? (
        <Flex align="center" gap="3" flexWrap="wrap">
          <Text fontSize="sm" color="smMuted">
            {t("The list could not be loaded.")}
          </Text>
          <Button variant="link" colorScheme="teal" size="sm" fontWeight="normal" onClick={() => void query.refetch()}>
            {t("Try again")}
          </Button>
        </Flex>
      ) : children.length === 0 ? (
        <Text fontSize="sm" color="smMuted">
          {emptyText}
        </Text>
      ) : (
        <Stack spacing="1">
          {children}
          {total > children.length && (
            <Button variant="ghost" size="sm" alignSelf="flex-start" onClick={onShowAll}>
              {t("Show all ({{count}})", { count: total })}
            </Button>
          )}
        </Stack>
      )}
    </Box>
  );
};

const PickRow = ({
  title,
  detail,
  onClick,
  isDisabled,
}: {
  title: string;
  detail?: string | null;
  onClick: () => void;
  isDisabled: boolean;
}) => (
  <Button
    variant="ghost"
    justifyContent="space-between"
    whiteSpace="normal"
    h="auto"
    py="2"
    fontWeight="normal"
    textAlign="left"
    onClick={onClick}
    isDisabled={isDisabled}
  >
    <Text as="span" color="smText">
      {title}
    </Text>
    {detail && (
      <Text as="span" fontSize="sm" color="smMuted" flexShrink={0} ml="3">
        {detail}
      </Text>
    )}
  </Button>
);

const NeedPicker = ({ onPick, isDisabled }: { onPick: (starting: Starting) => void; isDisabled: boolean }) => {
  const { t } = useTranslation();
  const query = useListNeedsQuery({
    status: [NeedStatus.Approved, NeedStatus.Planned, NeedStatus.ProcurementCreated].join(","),
  });
  const [showsAll, setShowsAll] = useState(false);
  const needs = (query.data?.needs ?? []).filter((need) => !need.procurement || need.procurement.unsavedInterview);
  return (
    <PickList
      heading={t("Needs ready for a procurement")}
      query={query}
      emptyText={t("No need is ready for a procurement.")}
      total={needs.length}
      onShowAll={() => setShowsAll(true)}
    >
      {(showsAll ? needs : needs.slice(0, PICK_LIMIT)).map((need) => {
        const title = need.title?.trim() || String(t("Untitled"));
        return (
          <PickRow
            key={need.id}
            title={title}
            onClick={() => onPick({ kind: "need", id: need.id, title })}
            isDisabled={isDisabled}
          />
        );
      })}
    </PickList>
  );
};

const EarlierPicker = ({ onPick, isDisabled }: { onPick: (starting: Starting) => void; isDisabled: boolean }) => {
  const { t, i18n } = useTranslation();
  const orgId = useLoggedInWithOrgContextState().selectedOrg.id;
  const [contractLimit, setContractLimit] = useState(PICK_LIMIT);
  const [procurementLimit, setProcurementLimit] = useState(PICK_LIMIT);
  const contracts = useListContractReportsQuery({
    q: base64Encode(
      JSON.stringify({ owningOrganizationId: orgId, statuses: [ContractStatusDto.Active], limit: contractLimit }),
    ),
    sortBy: ContractReportSortColumn.EndDate,
    sortDirection: SortDirection.Asc,
  });
  const procurements = useListBuyerEventsQuery({ orgId, limit: procurementLimit });
  const contractCount = contracts.data?.count ?? 0;
  const procurementCount = procurements.data?.count ?? 0;
  const locale = i18n.language === "en" ? "en" : "nb";
  return (
    <Stack spacing="3">
      <PickList
        heading={t("Contracts")}
        query={contracts}
        emptyText={t("No active contract.")}
        total={contractCount}
        onShowAll={() => setContractLimit(contractCount)}
      >
        {(contracts.data?.reports ?? []).map((contract) => (
          <PickRow
            key={contract.contractId}
            title={contract.title}
            detail={contract.endDate ? t("Ends {{date}}", { date: displayDate(contract.endDate, locale) }) : null}
            onClick={() => onPick({ kind: "contract", id: contract.contractId, title: contract.title })}
            isDisabled={isDisabled}
          />
        ))}
      </PickList>
      <PickList
        heading={t("Earlier procurements")}
        query={procurements}
        emptyText={t("No earlier procurement.")}
        total={procurementCount}
        onShowAll={() => setProcurementLimit(procurementCount)}
      >
        {(procurements.data?.events ?? []).map((event) => (
          <PickRow
            key={event.id}
            title={event.title}
            onClick={() => onPick({ kind: "procurement", id: event.id, title: event.title })}
            isDisabled={isDisabled}
          />
        ))}
      </PickList>
    </Stack>
  );
};
